import './styles.scss';

import { Button } from '@components/Button/Button';
import { Card } from '@components/common/Card/Card';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useLazyExportTaskQuery } from '@/api/report/api';
import {
  useCreateTaskMutation,
  useDeletePendingTaskMutation,
  useGetActiveTaskQuery,
  useLazyGetDrugsShortageQuery,
  useLazyGetTaskPendingIdQuery,
  usePersistPendingTaskMutation,
} from '@/api/vaccination/task/api';
import { ARCHIVE, MONITORING, PHARMACY, TASK, VACCIANTION } from '@/constants/routes';
import { orderCardConfig } from '@/pages/Vaccination/components/task/card.config';
import { drugColumns } from '@/pages/Vaccination/components/task/columns.config';
import { ProtocolEventsTable } from '@/pages/Vaccination/components/task/table/ProtocolEventsTable';
import { useAppNotification } from '@/shared/hooks/useAppNotification';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { withSecurity } from '@/shared/security/withSecurity';
import { downloadBlob } from '@/utils/utils';

import cl from './styles.module.scss';

export const VaccinationTask = withSecurity(({ auth }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showWarning } = useAppNotification();
  const authOrgId =
    (auth?.organization?.level === OrganizationLevelType.STRUCTURAL_DIVISION
      ? auth?.organization?.id
      : searchParams.get('organizationId')) || '';
  const orgId = searchParams.get('organizationId') || '';
  const orgName = searchParams.get('orgName') || '';

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDrugShortageModalOpen, setIsDrugShortageModalOpen] = useState<boolean>(false);

  const [triggerVaccinationTask] = useCreateTaskMutation();
  const [triggerPendingTask] = usePersistPendingTaskMutation();
  const [triggerDeletePendingTask] = useDeletePendingTaskMutation();

  const [getPendingTaskId, { data: pendingTaskId }] = useLazyGetTaskPendingIdQuery();
  const [getDrugsShortage, { data: drugsShortage }] = useLazyGetDrugsShortageQuery();

  const { data: activeTask, isFetching } = useGetActiveTaskQuery(
    { organizationId: authOrgId || '' },
    { skip: !authOrgId },
  );

  const [exportTaskReport, { isLoading: isDownloadReportLoading }] =
    useLazyExportTaskQuery();

  const handleDownloadReport = useCallback(async () => {
    if (activeTask) {
      const { organizationId, taskDate, status } = activeTask;
      const { data, meta } = await exportTaskReport(
        { filters: { organizationId, taskDate, status } },
        false,
      ).unwrap();

      if (meta.response?.status === 204) {
        showWarning('Внимание!', 'Отсутствуют данные для формирования файла');
        return;
      }

      const filename = `${orgName}_${taskDate}.pdf`;
      downloadBlob(data, filename);
    }
  }, [activeTask]);

  const handleCrateTask = async () => {
    if (authOrgId) {
      try {
        let pendingTaskId = await getPendingTaskId({
          organizationId: authOrgId || '',
        }).unwrap();
        if (pendingTaskId) {
          await getDrugsShortage(pendingTaskId);
          setIsDrugShortageModalOpen(true);
          return;
        }

        await triggerVaccinationTask().unwrap();
        pendingTaskId = await getPendingTaskId({
          organizationId: authOrgId || '',
        }).unwrap();
        if (pendingTaskId) {
          await getDrugsShortage(pendingTaskId);
          setIsDrugShortageModalOpen(true);
        }
      } catch (error: any) {
        console.error('Ошибка при формировании наряда');

        if (error?.data?.errorCode === 'VACCINATION_HAS_TASKS_WITHOUT_EXPENSED_ENTRIES') {
          setIsModalOpen(true);
        }
      }
    }
  };

  const canAction = useMemo(() => {
    return {
      createTask: hasPermission(auth, {
        permissions: vaccination.task.create,
        level: OrganizationLevelType.ORGANIZATION,
      }),
      export: hasPermission(auth, { permissions: vaccination.task.export }),
      viewArchive: hasPermission(auth, { permissions: vaccination.task.viewArchive }),
    };
  }, [auth]);

  const formConfirmPendingTaskText = () => {
    const shortages = drugsShortage
      ?.map(({ drugName, shortageVolume }) => `${drugName} (${shortageVolume})`)
      .join(', ');

    return (
      <span>
        Для формирования наряда на всех обязательных особей отсутствует препарат:
        <br />
        {shortages ?? ''}.
      </span>
    );
  };

  return (
    <div className={'vaccination-task'}>
      <Card
        styles={{ marginTop: 0 }}
        viewMode={true}
        schema={orderCardConfig}
        data={activeTask || {}}
      />
      <PageTitle level={4} title={'Препараты'} className={cl.title} subTitle={true} />
      <UTable
        size={'small'}
        data={activeTask?.drugs || []}
        columns={drugColumns(!activeTask?.haveDrugsShortage, activeTask?.drugs)}
        rowClassName={(record: any) => (record?.shortageVolume ? 'warning-row' : '')}
        loading={isFetching}
      />
      <div className={cl.buttonsWrap}>
        <div>
          <Button
            type="primary"
            text="Сформировать наряд"
            className={cl.formOrder}
            onClick={handleCrateTask}
            disabled={!authOrgId || !!activeTask}
            loading={isFetching}
            display={canAction.createTask}
          />
          <Button
            onClick={handleDownloadReport}
            type="primary"
            text="Экспорт в pdf"
            loading={isDownloadReportLoading}
            disabled={!activeTask}
            display={canAction.export}
          />
        </div>
        <Button
          type="primary"
          text="Архив"
          onClick={() =>
            navigate(VACCIANTION + TASK + ARCHIVE + `?organizationId=${orgId}`)
          }
          display={canAction.viewArchive}
        />
      </div>
      <ProtocolEventsTable data={activeTask?.protocols || []} />
      <Modal
        mode={'confirm'}
        open={isModalOpen}
        onSubmit={() => {}}
        onCancel={() => setIsModalOpen(false)}
        text={'Спишите препараты за предыдущий наряд'}
        footer={null}
      />
      <Modal
        mode={'confirm'}
        open={isDrugShortageModalOpen}
        width={600}
        strict
        onSubmit={() => {
          triggerPendingTask(pendingTaskId || '');
          setIsDrugShortageModalOpen(false);
        }}
        submitText={'Сформировать наряд'}
        onCancel={async () => {
          await triggerDeletePendingTask(pendingTaskId || '').unwrap();
          setIsDrugShortageModalOpen(false);
          navigate(PHARMACY + MONITORING + `?organizationId=${orgId}&orgName=${orgName}`);
        }}
        cancelText={'Заполнить аптеку'}
        text={formConfirmPendingTaskText()}
      />
    </div>
  );
});
